import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { Calendar, CreditCard, CheckCircle, AlertCircle, Clock, Download, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Payment = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);


  const fetchPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Using the exact API URL provided
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-payment-history`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.data)) {
        const mappedData = data.data.map(p => ({
          id: p.id,
          type: p.category?.category || 'Session Payment',
          amount: parseFloat(p.amount) || 0,
          dueDate: p.created_at ? p.created_at.split(' ')[0] : 'N/A',
          status: p.payment_status === "1" ? "paid" : "pending",
          description: [
            p.session?.session,
            p.shift?.shift_name,
            p.time_slot?.time
          ].filter(Boolean).join(' - ') || 'Payment',
          transactionId: p.payment_id || null, // Handle mapping properly
          paidDate: p.payment_status === "1" && p.updated_at ? p.updated_at.split(' ')[0] : null
        }));
        setPayments(mappedData);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const pendingPayments = payments.filter(payment => payment.status === 'pending');
  const paidPayments = payments.filter(payment => payment.status === 'paid');
  const totalDue = pendingPayments.reduce((sum, payment) => sum + payment.amount, 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="badge rounded-pill px-3 py-2 shadow-sm" style={{ backgroundColor: '#e6f4ea', color: '#1e8e3e', border: '1px solid #ceead6', fontWeight: '600', letterSpacing: '0.3px' }}>Paid</span>;
      case 'pending':
        return <span className="badge rounded-pill px-3 py-2 shadow-sm" style={{ backgroundColor: '#fef7e0', color: '#d97706', border: '1px solid #fde68a', fontWeight: '600', letterSpacing: '0.3px' }}>Pending</span>;
      case 'overdue':
        return <span className="badge rounded-pill px-3 py-2 shadow-sm" style={{ backgroundColor: '#fce8e6', color: '#d93025', border: '1px solid #fad2cf', fontWeight: '600', letterSpacing: '0.3px' }}>Overdue</span>;
      default:
        return <span className="badge rounded-pill px-3 py-2 shadow-sm" style={{ backgroundColor: '#f1f3f4', color: '#5f6368', border: '1px solid #dadce0', fontWeight: '600', letterSpacing: '0.3px' }}>Unknown</span>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="text-success" size={20} />;
      case 'pending':
        return <Clock className="text-warning" size={20} />;
      case 'overdue':
        return <AlertCircle className="text-danger" size={20} />;
      default:
        return null;
    }
  };

  const handleDownloadReceipt = (transactionId) => {
    // Handle receipt download logic here
    console.log('Downloading receipt for:', transactionId);
  };

  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-wrapper">
        <Sidebar activeItem="payment" />
        <div className="flex-grow-1 dashboard-content">
          <h2 className="page-title">Payment History</h2>
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Payment Success Banner */}
              {payments.some(payment => payment.status === 'paid') && (
                <div className="action-section mb-4">
                  <div className="alert mb-0 border-0 d-flex align-items-center gap-3" style={{ backgroundColor: '#e8f4fd', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ color: '#0d6efd' }}><CheckCircle size={24} /></div>
                    <div>
                      <strong className="d-block text-dark mb-1">Payment Complete</strong>
                      <span className="text-secondary small">Your payment is successful. You can download your receipt from the table below.</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment History Table */}
              <div className="card">
                <div className="card-header bg-white">
                  <div className="w-100 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Payment History</h5>
                    <button className="btn btn-sm btn-outline-primary" onClick={fetchPayments} disabled={loading}>
                      <RefreshCw size={16} className={`me-1 ${loading ? 'spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div style={{ borderRadius: '12px', border: '1px solid #f0f0f0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                    <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table className="table table-hover align-middle mb-0" style={{ minWidth: '1100px' }}>
                      <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <tr>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Type</th>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', width: '25%' }}>Description</th>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount</th>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Due Date</th>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Paid Date</th>
                          <th className="py-3 px-4 fw-semibold text-secondary border-0" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trans ID</th>
                        </tr>
                      </thead>
                      <tbody style={{ borderTop: 'none' }}>
                        {payments.map((payment) => (
                          <tr key={payment.id} style={{ transition: 'all 0.2s ease' }}>
                            <td className="py-3 px-4 border-bottom">
                              <div className="d-flex align-items-center gap-2">
                                {getStatusBadge(payment.status)}
                              </div>
                            </td>
                            <td className="py-3 px-4 border-bottom">
                              <div className="fw-bold text-dark">{payment.type}</div>
                            </td>
                            <td className="py-3 px-4 border-bottom">
                              <div className="text-secondary small" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={payment.description}>
                                {payment.description}
                              </div>
                            </td>
                            <td className="py-3 px-4 border-bottom">
                              <div className="fw-bold text-primary fs-6">₹{payment.amount.toLocaleString()}</div>
                            </td>
                            <td className="py-3 px-4 border-bottom">
                              <div className="d-flex align-items-center gap-2 text-secondary">
                                <span className={payment.dueDate === 'N/A' ? 'text-muted' : 'fw-medium'}>{payment.dueDate !== 'N/A' ? payment.dueDate : '-'}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 border-bottom">
                              <div className="d-flex align-items-center gap-2">
                                <span className={payment.paidDate ? "fw-medium text-dark" : "text-muted"}>{payment.paidDate ? payment.paidDate : '-'}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 border-bottom">
                              {payment.transactionId ? (
                                <span className="badge bg-light text-dark border font-monospace px-2 py-1" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                                  {payment.transactionId}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 border-bottom text-end">
                              <div className="d-flex gap-2 justify-content-end">

                                {payment.status === 'paid' && (
                                  <button 
                                    className="btn btn-outline-primary btn-sm rounded-circle d-flex align-items-center justify-content-center shadow-sm hover-elevate"
                                    style={{ width: '36px', height: '36px', transition: 'all 0.2s', backgroundColor: '#f8fafc' }}
                                    onClick={() => handleDownloadReceipt(payment.transactionId)}
                                    title="Download Receipt"
                                  >
                                    <Download size={16} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                  
                  {payments.length === 0 && (
                    <div className="text-center py-4">
                      <CreditCard size={48} className="text-muted mb-3" />
                      <p className="text-muted">No payment history available</p>
                    </div>
                  )}
                </div>
              </div>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payment;
